import { useState } from 'react';
import LogoZoomPopover from '@/components/LogoZoomPopover';

/**
 * CompanyIcon — Round 92o++++++++j (Feb 2026)
 *
 * Renders a small square company icon next to the ticker symbol,
 * proxied through our backend (which adds the Massive apiKey
 * server-side). Self-managed state ensures we NEVER show a broken
 * image placeholder — if the fetch fails for any reason (404 from
 * Massive, network error, unsupported ticker), the component
 * unmounts itself silently.
 *
 * Critical gotcha this component avoids: setting `display: none` on
 * an <img> at initial render causes Chromium to skip the fetch
 * entirely, so onLoad never fires. We use a `loaded` state flag and
 * keep the <img> mounted at low opacity (CSS invisible but still
 * fetched), then fade it in once onLoad fires. If onError fires
 * instead, we unmount via `errored` state.
 *
 * Feb 2026 — `zoomable` prop: when set, hover shows a zoom-in
 * cursor and click opens a 280px preview near the pointer so the
 * tiny header icon is actually readable.
 */
export default function CompanyIcon({ ticker, size = 20, className = '', zoomable = false }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [zoomAnchor, setZoomAnchor] = useState(null);

  if (!ticker || errored) return null;

  const src = `${process.env.REACT_APP_BACKEND_URL}/api/stocks/${encodeURIComponent(ticker.toUpperCase())}/icon`;

  return (
    <>
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        width={size}
        height={size}
        data-testid="modal-company-icon"
        className={`rounded-sm shrink-0 bg-transparent transition-opacity duration-200 ${zoomable ? 'cursor-zoom-in hover:ring-2 hover:ring-violet-400/60' : ''} ${className}`}
        style={{
          width: size,
          height: size,
          // Render at 0 opacity (not display:none) so Chromium actually
          // performs the fetch. Once onLoad fires we flip to 1.
          opacity: loaded ? 1 : 0,
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        onClick={zoomable ? (e) => { e.stopPropagation(); setZoomAnchor({ x: e.clientX, y: e.clientY }); } : undefined}
        title={zoomable ? `Click to enlarge ${ticker} icon` : undefined}
      />
      {zoomable && zoomAnchor && (
        <LogoZoomPopover
          src={src}
          ticker={ticker}
          anchor={zoomAnchor}
          onClose={() => setZoomAnchor(null)}
        />
      )}
    </>
  );
}
